import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
import { productShortName } from '../data/offer';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClasses = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-navy shadow-md transition-colors duration-150 ease-out hover:bg-pearl focus:outline-none focus-visible:ring-4 focus-visible:ring-aqua/30';
  return <section className="bg-white py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-99" data-mp="1we94v4f7agi6">
      <div className="mx-auto max-w-6xl px-4" data-id="element-100" data-mp="3vo7i3q2gdzse">
        <div className="text-center" data-id="element-101" data-mp="3qpe5s0ywkett">
          <p className="text-sm font-semibold text-aqua" data-id="element-102" data-mp="3qqfhm7nqt5cl">Look Inside</p>
          <h2 id="inside-title" className="mx-auto mt-3 max-w-4xl text-balance text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl" data-id="element-103" data-mp="1p0gkcthay20j">
            Discover What&apos;s Inside <span className="text-aqua" data-id="element-104" data-mp="1f2u5g7006agk">{productShortName}</span>
          </h2>
          <span className="mx-auto mt-4 flex w-fit items-center gap-1" aria-hidden="true" data-id="element-105" data-mp="1f0prew8e3v2k">
            <span className="h-1 w-10 rounded-full bg-aqua/40" data-id="element-106" data-mp="1jzasexvguis2" />
            <span className="h-1 w-3 rounded-full bg-gold" data-id="element-107" data-mp="27sj6ulgmkcip" />
          </span>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-gray-600" data-id="element-108" data-mp="2cqkrhatp8jwa">
            Visual pages with clear process flows, cleanroom layouts, quality-system flowcharts, worked examples, GMP
            warnings and shop-floor takeaways. Here are some real pages from Volume 1.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-109" data-mp="nabh19zl50vn">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth px-[10%] pb-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" data-id="element-110" data-mp="rejb7motb919">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-xl border bg-pearl shadow-md transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-gold opacity-100' : 'border-gray-200 opacity-60'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-111" data-mp="tqbq6n2rc8dq" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" draggable={false} className="block aspect-[4/5] w-full select-none object-cover" data-id="element-112" data-mp="asin9ks4vygc" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className={`${arrowClasses} left-0 sm:-left-2`} aria-label="Previous page" data-id="element-113" data-mp="3ehvqcfin9ob1">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-114" data-mp="39hpv6pd61jo7" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className={`${arrowClasses} right-0 sm:-right-2`} aria-label="Next page" data-id="element-115" data-mp="2cs6xhzm04csv">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-116" data-mp="2gt8wcfc0amub" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-117" data-mp="2lqr5o6txr42u">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-aqua ${index === active ? 'w-6 bg-gold' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-118" data-mp="2qk3k5o4whuhv" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}